import React, { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createAssessmentInvite, assessmentTakeUrl, apiError } from '../../services/assessmentApi';
import { useRegion } from '../../context/RegionContext';

const EMPTY = { candidate_name: '', candidate_email: '', expires_in_days: '7', min_skills: 'default' };

export const InviteModal = ({ open, onOpenChange, testId, onCreated, defaultMin = 4, maxSkills = 10, baseMin = 4 }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const { region } = useRegion();
  const mins = Array.from({ length: maxSkills - baseMin + 1 }, (_, i) => String(baseMin + i));

  const reset = (v) => { if (!v) { setLink(''); setForm(EMPTY); } onOpenChange(v); };
  const create = async () => {
    setBusy(true);
    try {
      const inv = await createAssessmentInvite(testId, {
        ...form, region, expires_in_days: Number(form.expires_in_days), min_skills: form.min_skills === 'default' ? null : Number(form.min_skills),
      });
      setLink(assessmentTakeUrl(inv.token));
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e, 'Could not create link'));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => { await navigator.clipboard.writeText(link).catch(() => {}); toast.success('Personal link copied'); };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-md" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle>Create a personal test link</DialogTitle>
          <DialogDescription>One candidate, one attempt. Their details are pre-filled on the test page.</DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm text-emerald-600 font-medium"><Check className="h-4 w-4" /> Link ready for {form.candidate_name}</p>
            <div className="flex gap-2">
              <Input readOnly value={link} className="font-mono text-xs" data-testid="invite-link-output" />
              <Button onClick={copy} className="gap-1.5" data-testid="invite-copy-link-btn"><Copy className="h-4 w-4" /> Copy</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Input placeholder="Candidate full name" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="invite-name-input" />
            <Input placeholder="Candidate email (optional)" type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="invite-email-input" />
            <Select value={form.expires_in_days} onValueChange={(v) => setForm({ ...form, expires_in_days: v })}>
              <SelectTrigger data-testid="invite-expiry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['3', '7', '14', '30'].map((d) => <SelectItem key={d} value={d}>Expires in {d} days</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">Minimum skills to pick</p>
              <Select value={form.min_skills} onValueChange={(v) => setForm({ ...form, min_skills: v })}>
                <SelectTrigger data-testid="invite-min-skills-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default" data-testid="invite-min-skills-option-default">Test default ({defaultMin} skills)</SelectItem>
                  {mins.map((n) => <SelectItem key={n} value={n} data-testid={`invite-min-skills-option-${n}`}>{n} skills{n === String(maxSkills) ? ' (all 10)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          {link ? <Button variant="outline" onClick={() => reset(false)} data-testid="invite-done-btn">Done</Button> : (
            <Button onClick={create} disabled={busy || form.candidate_name.trim().length < 2} data-testid="invite-create-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Generate link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteModal;
